// precoLivro.js — o preço de um livro, lido do jeito certo, num lugar só.
//
// ================= POR QUE ESTE ARQUIVO EXISTE =================
//
// Todo card do site que tem botão de carrinho monta o item por conta própria.
// Eram oito pontos de `adicionarItem`, e cada um decidia sozinho qual campo era
// "o preço". Enquanto só existia `livro.preco`, isso funcionava por sorte.
//
// Com o desconto dos destaques passou a existir um segundo campo, e a sorte
// acabou: um card que mostre o preço com desconto e mande o cheio (ou o
// contrário) faz o checkout recusar o pedido inteiro com "o preço de um dos
// livros não confere". O servidor está certo em recusar — ele compara com o
// preço que ele mesmo calculou. Errado é cada tela ter a sua opinião.
//
// Então a opinião passa a ser uma só, e mora aqui.
//
// ================= O QUE O SERVIDOR MANDA =================
//
// `App\Services\PrecoService::decorar()` pendura três campos em todo livro que
// sai da API, SEMPRE — inclusive quando não há desconto nenhum:
//
//   preco_original       o preço cheio, de `ebooks.preco`
//   preco_com_desconto   o que se cobra hoje
//   desconto_percentual  0 quando não há desconto
//
// Eles irem sempre, e não só quando há promoção, é de propósito: campo que às
// vezes existe obriga cada tela a inventar a própria checagem, e é aí que uma
// delas erra. Ainda assim as funções abaixo toleram a ausência — endpoints
// antigos, itens salvos no carrinho antes desta mudança, cache do navegador.

/** Número > 0? (o catálogo tem livro só-impresso, com `preco` nulo) */
const valido = (p) => p != null && !isNaN(parseFloat(p)) && parseFloat(p) > 0;

/** O preço cheio do e-book, ou null se o livro não vende digital. */
export const precoCheio = (livro) => {
  const p = livro?.preco_original ?? livro?.preco;
  return valido(p) ? parseFloat(p) : null;
};

/**
 * O PREÇO QUE SE COBRA. É este que vai para o carrinho, sempre.
 *
 * Cai para o cheio quando o servidor não mandou o campo — nunca inventa
 * desconto por conta própria.
 */
export const precoDeVenda = (livro) => {
  const cheio = precoCheio(livro);
  const comDesconto = livro?.preco_com_desconto;
  if (valido(comDesconto)) {
    const v = parseFloat(comDesconto);
    // Maior que o cheio seria dado inconsistente; nesse caso o cheio manda.
    if (cheio == null || v <= cheio) return v;
  }
  return cheio;
};

/** Há desconto de verdade neste livro? */
export const temDesconto = (livro) => {
  const cheio = precoCheio(livro);
  const venda = precoDeVenda(livro);
  return cheio != null && venda != null && venda < cheio;
};

/** Quantos por cento saíram. Zero quando não há desconto. */
export const percentualOff = (livro) => {
  if (!temDesconto(livro)) return 0;
  const declarado = Number(livro?.desconto_percentual);
  // O número do servidor manda; a conta local é só para dados antigos.
  if (declarado > 0) return Math.round(declarado);
  return Math.round((1 - precoDeVenda(livro) / precoCheio(livro)) * 100);
};

/**
 * Os campos de preço de um item de carrinho.
 *
 * `preco` é o que se cobra. `preco_original` só aparece quando há desconto, e
 * serve para o CARRINHO e o CHECKOUT poderem riscar o valor antigo sem
 * consultar o servidor de novo.
 *
 * Por que o "de" viaja com o item, em vez de ser buscado depois: o carrinho é
 * uma lista guardada no navegador, montada em telas diferentes. Se ele
 * precisasse do livro para saber o preço cheio, seriam N requisições ao abrir
 * a sacola — e o carrinho é justamente a tela que não pode demorar.
 *
 * Item salvo antes desta mudança simplesmente não tem o campo, e aí a linha
 * aparece sem o riscado. É a degradação certa: nunca inventa um "de" que não
 * veio do servidor.
 */
export const camposDePreco = (livro) => {
  const venda = precoDeVenda(livro);
  const cheio = precoCheio(livro);
  return venda != null && cheio != null && venda < cheio
    ? { preco: venda, preco_original: cheio }
    : { preco: venda };
};

/** "39,90" — sem o "R$", que cada tela põe do seu jeito. */
export const fmtPreco = (v) =>
  (Number(v) || 0).toFixed(2).replace(".", ",");

export default precoDeVenda;
